/**
 * Server state for build step 3 — client requests, internal requests (Sales / Marketing → Ops), My requests,
 * notifications, escalations and client tasks. Every read and write is a contract route (routes.step3).
 *
 * Query keys live under ['work', …] so the step-2 `refreshAfterWrite` (every work write) also refreshes these,
 * and every write here refreshes the Work list, Today, the menu badges and the client counts in turn.
 */
import { useQuery } from '@tanstack/react-query';
import { REQUEST_FILE_MAX_BYTES, type RouteQuery } from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../api';
import { todayIso } from './format';
import { addDays, daysBetween } from './work';
import { openBlankTab } from './tab';

export const rkeys = {
  clientRequest: (id: string) => ['work', 'client-request', id] as const,
  clientRequests: (q: object) => ['work', 'client-requests', q] as const,
  internalRequest: (id: string) => ['work', 'internal-request', id] as const,
  options: ['work', 'internal-request-options'] as const,
  myRequests: ['work', 'my-requests'] as const,
  escalation: (id: string) => ['work', 'escalation', id] as const,
  escalations: (q: object) => ['work', 'escalations', q] as const,
  clientTask: (id: string) => ['work', 'client-task', id] as const,
  clientTasks: (q: object) => ['work', 'client-tasks', q] as const,
};

/** 404/403 are answers, not glitches: never retry them. */
const noRetryOnScope = (n: number, e: unknown) => !(e instanceof ApiFailure && (e.status === 404 || e.status === 403)) && n < 2;

type Paged<T> = { data: T[]; next_cursor: string | null };
async function allPages<T>(fetchPage: (cursor: string | undefined) => Promise<Paged<T>>): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await fetchPage(cursor);
    out.push(...page.data);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  return out;
}

// ---------------------------------------------------------------- client requests

export const useClientRequest = (id: string | null, enabled = true) =>
  useQuery({
    queryKey: rkeys.clientRequest(id ?? ''),
    queryFn: () => call('clientRequests.get', { params: { id: id! } }),
    enabled: !!id && enabled,
    retry: noRetryOnScope,
  });

export const useClientRequests = (q: RouteQuery<'clientRequests.list'>, enabled = true) =>
  useQuery({
    queryKey: rkeys.clientRequests(q),
    queryFn: () => allPages((cursor) => call('clientRequests.list', { query: { ...q, limit: 200, cursor } as RouteQuery<'clientRequests.list'> })),
    enabled,
  });

// ---------------------------------------------------------------- internal requests

export const useInternalRequest = (id: string | null, enabled = true) =>
  useQuery({
    queryKey: rkeys.internalRequest(id ?? ''),
    queryFn: () => call('internalRequests.get', { params: { id: id! } }),
    enabled: !!id && enabled,
    retry: noRetryOnScope,
  });

export const useRequestOptions = (enabled = true) =>
  useQuery({ queryKey: rkeys.options, queryFn: () => call('internalRequests.options'), enabled, staleTime: 60_000 });

/** The "Typical turnaround" tile value: "about 1.4 days" from the median (one decimal); a same-day median reads "under a day". */
export function turnaroundText(medianDays: number): string {
  if (medianDays < 1) return 'under a day';
  const n = Math.round(medianDays * 10) / 10;
  return `about ${Number.isInteger(n) ? n : n.toFixed(1)} ${n === 1 ? 'day' : 'days'}`;
}

/** GET /me/requests — the requester's own requests (latest 200), their updates and unread count, and the stat-strip counts (`stats`, over ALL own requests) and `typical_turnaround`. */
export const useMyRequests = (enabled = true) =>
  useQuery({ queryKey: rkeys.myRequests, queryFn: () => call('me.requests'), enabled });

// ---------------------------------------------------------------- escalations and client tasks

export const useEscalation = (id: string | null) =>
  useQuery({
    queryKey: rkeys.escalation(id ?? ''),
    queryFn: () => call('escalations.get', { params: { id: id! } }),
    enabled: !!id,
    retry: noRetryOnScope,
  });

export const useEscalations = (q: RouteQuery<'escalations.list'>, enabled = true) =>
  useQuery({
    queryKey: rkeys.escalations(q),
    queryFn: () => allPages((cursor) => call('escalations.list', { query: { ...q, limit: 200, cursor } as RouteQuery<'escalations.list'> })),
    enabled,
  });

export const useClientTask = (id: string | null) =>
  useQuery({
    queryKey: rkeys.clientTask(id ?? ''),
    queryFn: () => call('clientTasks.get', { params: { id: id! } }),
    enabled: !!id,
    retry: noRetryOnScope,
  });

export const useClientTasks = (q: RouteQuery<'clientTasks.list'>, enabled = true) =>
  useQuery({
    queryKey: rkeys.clientTasks(q),
    queryFn: () => allPages((cursor) => call('clientTasks.list', { query: { ...q, limit: 200, cursor } as RouteQuery<'clientTasks.list'> })),
    enabled,
  });

// ---------------------------------------------------------------- words and clocks (prototype helpers)

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "8 Oct" from a business date. */
export function shortDay(iso: string): string {
  return `${+iso.slice(8, 10)} ${MON[+iso.slice(5, 7) - 1]}`;
}

/** Prototype `needLabel(d)`: "Today", "Tomorrow", else "8 Oct (Thu)" — d days from today. */
export function needLabel(days: number, today = todayIso()): string {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return dateNeedLabel(addDays(today, days), today);
}

/** The same words for a date: "Today", "Tomorrow", "8 Oct (Thu)". */
export function dateNeedLabel(iso: string, today = todayIso()): string {
  const d = daysBetween(today, iso);
  if (d === 0) return 'Today';
  if (d === 1) return 'Tomorrow';
  const dow = DOW[new Date(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10))).getUTCDay()];
  return `${shortDay(iso)} (${dow})`;
}

/** Prototype `nlc()`: the same label, lower-case Today/Tomorrow for use mid-sentence. */
export const needLower = (s: string) => (s === 'Today' || s === 'Tomorrow' ? s.toLowerCase() : s);

/** Prototype `slaTxt()`: "6h past" / "18h left" (whole hours). */
export function slaText(hours: number): string {
  const h = Math.round(hours);
  return h < 0 ? `${-h}h past` : `${h}h left`;
}

/** Work-row clock chip (prototype `reqClock`): "6h past time" cold, "≤24h left" warm, "3d left" from 48h. */
export function rowClock(hours: number): { label: string; tone: 'cold' | 'warm' | '' } {
  const h = Math.round(hours);
  if (h < 0) return { label: `${-h}h past time`, tone: 'cold' };
  if (h <= 24) return { label: `${h}h left`, tone: 'warm' };
  if (h >= 48) return { label: `${Math.floor(h / 24)}d left`, tone: '' };
  return { label: `${h}h left`, tone: '' };
}

/** "Today 9:10 am" / "Yesterday 5:30 pm" / "3 Oct 4:05 pm" — a message or note time, in Asia/Kolkata. */
export function whenText(ts: string): string {
  const d = new Date(ts);
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(d);
  const time = new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true })
    .format(d)
    .replace(/\s?([AP])M$/i, (_m, p: string) => ` ${p.toLowerCase()}m`);
  const today = todayIso();
  const diff = daysBetween(day, today);
  const lead = diff === 0 ? 'Today' : diff === 1 ? 'Yesterday' : shortDay(day);
  return `${lead} ${time}`;
}

/** "Mon 5 Oct, 9:40 am" — a notification time. */
export function notifText(ts: string): string {
  const d = new Date(ts);
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(d);
  const dow = DOW[new Date(Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10))).getUTCDay()];
  const time = new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true })
    .format(d)
    .replace(/\s?([AP])M$/i, (_m, p: string) => ` ${p.toLowerCase()}m`);
  return `${dow} ${shortDay(day)}, ${time}`;
}

/** Severity chip (prototype `SEV`). */
export const SEVERITY: Record<'high' | 'med' | 'low', { tone: 'cold' | 'warm' | ''; label: string }> = {
  high: { tone: 'cold', label: 'High' },
  med: { tone: 'warm', label: 'Medium' },
  low: { tone: '', label: 'Low' },
};

/** Status chip tones for internal requests (prototype `RSTAT`). */
export const IR_TONE: Record<string, string> = {
  waiting_triage: 'warm',
  in_progress: 'brand',
  needs_details: 'info',
  waiting_confirm: 'warm',
  done: 'hot',
  declined: '',
};

/** Client-request status chip: `new` reads as open — no screen triages client requests. */
export function clientReqStatus(s: 'new' | 'open' | 'needs_details' | 'done'): { tone: string; label: string } {
  if (s === 'done') return { tone: 'hot', label: 'Done' };
  if (s === 'needs_details') return { tone: 'info', label: 'Needs details' };
  return { tone: 'brand', label: 'Open' };
}

/** Internal-request status meaning (prototype drawer note). */
export const IR_MEANING: Record<string, string> = {
  waiting_triage: 'Ops has your request and will pick an owner within 24 hours.',
  in_progress: 'Someone in Ops is working on it.',
  needs_details: 'Ops asked you a question. The clock is paused until you reply.',
  waiting_confirm: 'Ops says it is done. Please confirm, or tell them what is missing.',
  done: 'Closed.',
  declined: 'Ops could not take it. The reason is in the conversation.',
};

export const isOpenIR = (s: string) => s === 'waiting_triage' || s === 'in_progress' || s === 'needs_details';

// ---------------------------------------------------------------- files on an internal request (S3-X-04)

/** At most 5 files per request (CreateInternalRequestBody.file_keys). */
export const REQUEST_FILE_MAX_COUNT = 5;
/** The contract's own wording for an oversized file (SignRequestUploadBody.bytes). */
export const REQUEST_FILE_TOO_BIG = 'The file is over 25 MB. Compress it, split it or paste a link.';
export const requestFileTooBig = (f: File) => f.size > REQUEST_FILE_MAX_BYTES;

/**
 * POST /internal-requests/sign-upload, then PUT the file straight to storage with the slot's headers. Returns the
 * key to send in `file_keys`. A refused PUT is an ApiFailure, so the pop-up shows it like any other failure.
 */
export async function uploadRequestFile(file: File): Promise<string> {
  const slot = await call('internalRequests.signUpload', {
    idempotencyKey: newIdempotencyKey(),
    body: { filename: file.name, bytes: file.size, content_type: file.type || 'application/octet-stream' },
  });
  let res: Response;
  try {
    res = await fetch(slot.url, { method: slot.method, headers: slot.headers, body: file });
  } catch {
    throw new ApiFailure(0, null);
  }
  if (!res.ok) throw new ApiFailure(res.status, (await res.json().catch(() => null)) as never);
  return slot.key;
}

/** GET /internal-requests/{id}/files/{n}: a 10-minute link, fetched on click (never on render, it expires). */
export async function openRequestFile(requestId: string, n: number): Promise<void> {
  const tab = openBlankTab();
  try {
    const link = await call('internalRequests.fileUrl', { params: { id: requestId, n: String(n) } });
    if (tab) tab.location.href = link.url;
    else window.location.href = link.url;
  } catch (e) {
    tab?.close();
    throw e;
  }
}
